/** 招标信息筛选模块类型定义 */

export type ScreeningDecision = 'undecided' | 'go' | 'cautious' | 'no_go';

export type ScreeningVerdict = 'satisfied' | 'partial' | 'unsatisfied' | 'unknown';

export type ScreeningRiskLevel = 'pass' | 'warning' | 'high_risk' | 'unknown';

export type ScreeningRequirementCategory =
  | 'qualification'
  | 'performance'
  | 'personnel'
  | 'financial'
  | 'credit'
  | 'other';

export type ScreeningStatus = 'draft' | 'parsed' | 'evaluated';

/** 批量初筛结论 */
export type ScreeningPrescreenStatus = 'none' | 'keep' | 'unsure' | 'drop';

/** 招标实际结果 */
export type ScreeningOutcome = 'unknown' | 'won' | 'lost' | 'abandoned';

export type ScreeningSourceType = 'text' | 'file' | 'url';

/** 逐条要求与判定结果 */
export interface ScreeningRequirement {
  id: number;
  opportunity_id: number;
  seq: number;
  category: ScreeningRequirementCategory;
  requirement: string;
  is_mandatory: boolean;
  /** 是否客观分（可凭材料核验、评委无裁量空间）；客观分失分是投标决策的主要依据 */
  is_objective: boolean;
  /** 评分项分值上限（0 表示公告未给出分值） */
  score_weight: number;
  source_quote: string | null;
  llm_verdict: ScreeningVerdict;
  llm_evidence: string | null;
  risk_level: ScreeningRiskLevel;
  confidence: string | null;
  final_verdict: ScreeningVerdict | null;
  final_note: string | null;
  effective_verdict: ScreeningVerdict;
}

/** 单条评分项的预计得分与失分（客观分失分是投标决策的核心依据） */
export interface ScreeningScoringItem {
  seq: number;
  category: ScreeningRequirementCategory;
  requirement: string;
  is_objective: boolean;
  /** price 价格项（报价可控，不计入判据）/ objective 客观分 / subjective 主观分 */
  bucket?: 'objective' | 'subjective' | 'price';
  verdict: ScreeningVerdict;
  max_score: number;
  expect_score: number;
  lost_score: number;
  /** 失分性质：none 未失分 / evidence_missing 资料缺证明（可补）/ not_satisfied 明确不具备（不可补） */
  lost_type: 'none' | 'evidence_missing' | 'not_satisfied';
  reason: string | null;
}

export const LOST_TYPE_LABELS: Record<ScreeningScoringItem['lost_type'], string> = {
  none: '未失分',
  evidence_missing: '资料缺证明（可补）',
  not_satisfied: '明确不具备（不可补）',
};

export const LOST_TYPE_CLASSES: Record<ScreeningScoringItem['lost_type'], string> = {
  none: 'border-gray-200 bg-gray-50 text-gray-600',
  evidence_missing: 'border-amber-200 bg-amber-50 text-amber-700',
  not_satisfied: 'border-rose-200 bg-rose-50 text-rose-700',
};

/** 客观分失分明细（随评估结论一起保存，用于追溯「分扣在哪里」） */
export interface ScreeningScoringDetail {
  scoring_mode?: ScreeningScoringMode;
  /** 主观分（技术方案等）预估失分，不计入决策判据 */
  subjective_lost?: number;
  /** 价格部分（报价得分）预估失分，报价可控，不计入决策判据 */
  price_lost?: number;
  /** 失分中「资料缺证明、补齐可挽回」的部分 */
  evidence_missing_lost?: number;
  /** 失分中「明确不具备、补材料也无效」的部分 */
  not_satisfied_lost?: number;
  items?: ScreeningScoringItem[];
}

/** 一次评估结论 */
export interface ScreeningEvaluation {
  id: number;
  opportunity_id: number;
  version: number;
  conclusion: ScreeningDecision;
  total_score: number;
  /** 本次判定所用的价格分满分 */
  price_score: number | null;
  /** 客观分预估失分（资质/业绩/人员/财务/信用类评分项失分合计） */
  objective_lost_score: number;
  /** 客观分满分合计 */
  objective_max_score: number;
  /** 容忍的客观分失分额度 = 价格分 × 20% */
  tolerance_score: number | null;
  scoring_detail: ScreeningScoringDetail;
  dimension_scores: Partial<Record<'compliance' | 'competition' | 'resource' | 'return', number>>;
  advantages: string | null;
  disadvantages: string | null;
  risks: string[];
  missing_info: string[];
  invest_estimate: string | null;
  summary_md: string | null;
  model_name: string | null;
  created_at: string | null;
}

/** 多企业审核的计分口径 */
export type ScreeningScoringMode = 'weighted' | 'equal' | 'mandatory_only';

/** 硬性门槛三态：明确通过 / 存在无法确认项 / 存在不满足项 */
export type ScreeningGateStatus = 'passed' | 'unconfirmed' | 'failed';

export const GATE_LABELS: Record<ScreeningGateStatus, string> = {
  passed: '硬性门槛已明确通过',
  unconfirmed: '资格待确认',
  failed: '不合格（门槛不满足）',
};

export const GATE_CLASSES: Record<ScreeningGateStatus, string> = {
  passed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  unconfirmed: 'border-amber-200 bg-amber-50 text-amber-700',
  failed: 'border-rose-200 bg-rose-50 text-rose-700',
};

/** 某家企业对某条要求的评分明细 */
export interface ScreeningCompanyScoreItem {
  /** 明细行 ID（用于按企业逐条人工确认） */
  id: number;
  seq: number;
  category: ScreeningRequirementCategory;
  requirement: string;
  is_mandatory: boolean;
  max_score: number;
  /** 模型本次对该企业该条的判定 */
  verdict: ScreeningVerdict;
  /** 该企业该条的人工确认判定（企业级：只对该企业生效，重跑审核时保留并优先） */
  final_verdict?: ScreeningVerdict | null;
  /** 人工备注（企业级） */
  final_note?: string | null;
  /** 最终判定：人工确认优先（= final_verdict ?? verdict） */
  effective_verdict?: ScreeningVerdict;
  score: number;
  evidence: string | null;
  risk_level: ScreeningRiskLevel;
  /** manual：该企业该条为人工确认判定（模型未改判）；model：模型本次判定 */
  verdict_source?: 'manual' | 'model';
}

/** 某个企业某条客观分项为什么丢分（回答"分扣在哪里"） */
export interface ScreeningObjectiveLossItem {
  seq: number;
  category: ScreeningRequirementCategory;
  requirement: string;
  verdict: ScreeningVerdict;
  max_score: number;
  lost_score: number;
  expect_score: number;
  lost_type: 'none' | 'evidence_missing' | 'not_satisfied';
  reason: string | null;
}

/** 某家企业的「是否适合投标」审核结果（含逐条判定） */
export interface ScreeningCompanyResult {
  company_profile_id: number;
  company_name: string;
  rank: number;
  /** 硬性门槛是否存在不满足项（false 即一票否决；注意「无法确认」也算 true） */
  qualified: boolean;
  /** 门槛三态：明确全部通过 / 存在无法确认项 / 存在不满足项 */
  gate_status: ScreeningGateStatus;
  /** 硬性门槛是否已全部「明确」满足（待确认不算） */
  gates_confirmed: boolean;
  /** 是否适合投标的审核结论（门槛不满足 → no_go；门槛待确认最多 cautious）；历史记录可能为 null */
  conclusion?: ScreeningDecision | null;
  /** 客观分（资质/业绩/人员/财务/信用）预估失分 */
  objective_lost?: number | null;
  /** 客观分满分合计 */
  objective_max?: number | null;
  /** 容忍的客观分失分额度 = 价格分 × 20% */
  tolerance_score?: number | null;
  /** 公告识别到的价格分（0 表示未识别） */
  price_score?: number | null;
  /** 判定时实际采用的价格分（未识别时按常见 30 分估算） */
  effective_price_score?: number | null;
  /** 主观分（技术方案等）失分，不计入判据 */
  subjective_lost?: number | null;
  /** 失分中「资料缺证明、补齐可挽回」的部分 */
  evidence_missing_lost?: number | null;
  /** 失分中「明确不具备、补材料也无效」的部分 */
  not_satisfied_lost?: number | null;
  /** 是否适合投标的判定说明 */
  loss_note?: string | null;
  /** 客观分失分明细（只含确有失分的客观分项） */
  objective_loss_items?: ScreeningObjectiveLossItem[];
  /** 评标得分（0-100，仅作横向参考；资料不足不计分的保守口径） */
  total_score: number;
  /** 资料补齐后可能达到的得分 */
  potential_score: number;
  scoring_total: number;
  scoring_max: number;
  mandatory_total: number;
  mandatory_failed: number;
  mandatory_uncertain: number;
  /** 本次审核中该企业采用「人工确认判定」的条目数（企业级确认，非模型判定） */
  manual_confirmed?: number;
  strengths: string[];
  weaknesses: string[];
  summary: string | null;
  items: ScreeningCompanyScoreItem[];
}

/** 一次多企业资质对比与是否适合投标审核结果 */
export interface ScreeningCompanyComparison {
  id: number;
  opportunity_id: number;
  company_ids: number[];
  company_count: number;
  requirement_count: number;
  scoring_mode: ScreeningScoringMode;
  conclusion: ScreeningDecision;
  recommended_company_id: number | null;
  recommended_company_name: string | null;
  recommendation: string | null;
  risks: string[];
  summary_md: string | null;
  model_name: string | null;
  created_at: string | null;
  /** 本次审核中采用「人工确认判定」的「企业 × 条目」单元格数 */
  manual_confirmed?: number;
  /** 参评企业及其逐条判定（按名次排序） */
  companies: ScreeningCompanyResult[];
  /** 要求快照（审核矩阵的行） */
  requirements: Array<{
    seq: number;
    category: ScreeningRequirementCategory;
    requirement: string;
    is_mandatory: boolean;
    max_score: number;
  }>;
}

/** 最近一次「是否适合投标」审核的摘要（列表/详情头部展示用，不含逐条明细） */
export interface ScreeningComparisonSummary {
  id: number;
  conclusion: ScreeningDecision;
  company_count: number;
  created_at: string | null;
  recommended_company_id: number | null;
  recommended_company_name: string | null;
  /** 名次第 1 的企业（得分与结论与审核批次同源） */
  best_company_name: string | null;
  best_total_score: number | null;
  best_conclusion: ScreeningDecision | null;
  best_rank: number | null;
}

/** 招标信息（筛选对象） */
export interface ScreeningOpportunity {
  id: number;
  user_id: number;
  name: string;
  tenderer: string | null;
  agency: string | null;
  region: string | null;
  industry: string | null;
  budget: string | null;
  budget_source: string | null;
  duration: string | null;
  deposit: string | null;
  bid_deadline: string | null;
  open_time: string | null;
  qualification_summary: string | null;
  scoring_summary: string | null;
  /** 价格分满分（容忍客观分失分 = 价格分 × 20%）；0 表示公告未识别到价格分 */
  price_score?: number;
  source_type: ScreeningSourceType;
  source_url: string | null;
  source_site: string | null;
  source_file_name: string | null;
  fetch_status: string;
  fetch_error: string | null;
  fetched_at: string | null;
  has_raw_text: boolean;
  raw_text_length: number;
  raw_text?: string | null;
  prescreen_status: ScreeningPrescreenStatus;
  prescreen_score: number | null;
  prescreen_reason: string | null;
  status: ScreeningStatus;
  /** 结论：由最近一次审核回写（go=适合投标 / cautious=谨慎投标 / no_go=不适合投标） */
  decision: ScreeningDecision;
  outcome: ScreeningOutcome;
  outcome_note: string | null;
  outcome_at: string | null;
  company_profile_id: number | null;
  converted_project_id: number | null;
  created_at: string | null;
  updated_at: string | null;
  attachments?: ScreeningAttachment[];
  requirement_count?: number;
  requirements?: ScreeningRequirement[];
  evaluations?: ScreeningEvaluation[];
  /** 已下线的单企业评估（仅历史记录，详情页只读展示） */
  latest_evaluation?: ScreeningEvaluation | null;
  /** 最近一次「是否适合投标」审核摘要（与 decision 同源） */
  latest_comparison?: ScreeningComparisonSummary | null;
}

/** 公告附件（页面内 PDF/Word，已抓取并纳入判定） */
export interface ScreeningAttachment {
  name: string;
  url: string;
  file_type: string;
  ok: boolean;
  char_count: number;
  error: string;
}

/** URL 抓取预览结果 */
export interface FetchPreviewResult {
  status: string;
  url: string;
  final_url: string;
  content_type: string;
  title: string;
  site: string;
  char_count: number;
  preview: string;
  error: string;
  truncated: boolean;
  attachments: ScreeningAttachment[];
  attachment_count: number;
}

/** 文件文本提取结果 */
export interface ExtractFileResult {
  file_name: string;
  char_count: number;
  text: string;
}

/** 批量初筛单条结果 */
export interface ScreeningPrescreenItem {
  id: number;
  name: string;
  prescreen_status: ScreeningPrescreenStatus;
  prescreen_score: number | null;
  prescreen_reason: string | null;
  fetch_status: string;
  fetch_error: string | null;
}

/** 批量导入结果 */
export interface BatchImportResult {
  success: boolean;
  created: ScreeningOpportunity[];
  skipped: Array<{ url: string; reason: string }>;
  message: string;
}

/** 决策复盘统计 */
export interface ScreeningStats {
  total: number;
  by_decision: Record<ScreeningDecision, number>;
  by_outcome: Record<ScreeningOutcome, number>;
  by_prescreen: Record<ScreeningPrescreenStatus, number>;
  decided_samples: number;
  go_samples: number;
  go_hit: number;
  go_hit_rate: number | null;
  no_go_samples: number;
  no_go_miss: number;
  no_go_miss_rate: number | null;
  note: string;
}

/** SSE 流事件 */
export interface ScreeningStreamEvent {
  type?: 'progress' | 'result';
  message?: string;
  error?: boolean;
  opportunity_id?: number;
  requirements_count?: number;
  status?: string;
  opportunity?: ScreeningOpportunity;
  evaluation?: ScreeningEvaluation;
  comparison?: ScreeningCompanyComparison;
  items?: ScreeningPrescreenItem[];
  counts?: Record<string, number>;
}

export const DECISION_LABELS: Record<ScreeningDecision, string> = {
  undecided: '待评估',
  go: '建议投',
  cautious: '谨慎投',
  no_go: '建议不投',
};

/** 「是否适合投标」的结论表述（多企业审核回答的是「这家企业能不能去投」） */
export const BID_FIT_LABELS: Record<ScreeningDecision, string> = {
  undecided: '未审核',
  go: '适合投标',
  cautious: '谨慎投标',
  no_go: '不适合投标',
};

export const DECISION_CLASSES: Record<ScreeningDecision, string> = {
  undecided: 'bg-gray-100 text-gray-600 border-gray-200',
  go: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cautious: 'bg-amber-50 text-amber-700 border-amber-200',
  no_go: 'bg-rose-50 text-rose-700 border-rose-200',
};

export const VERDICT_OPTIONS: ScreeningVerdict[] = [
  'satisfied',
  'partial',
  'unsatisfied',
  'unknown',
];

export const VERDICT_LABELS: Record<ScreeningVerdict, string> = {
  satisfied: '满足',
  partial: '部分满足',
  unsatisfied: '不满足',
  unknown: '待确认',
};

export const VERDICT_CLASSES: Record<ScreeningVerdict, string> = {
  satisfied: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  partial: 'bg-amber-50 text-amber-700 border-amber-200',
  unsatisfied: 'bg-rose-50 text-rose-700 border-rose-200',
  unknown: 'bg-gray-100 text-gray-600 border-gray-200',
};

export const RISK_LABELS: Record<ScreeningRiskLevel, string> = {
  pass: '无风险',
  warning: '需关注',
  high_risk: '高风险',
  unknown: '未知',
};

export const RISK_CLASSES: Record<ScreeningRiskLevel, string> = {
  pass: 'text-emerald-600',
  warning: 'text-amber-600',
  high_risk: 'text-rose-600',
  unknown: 'text-gray-500',
};

export const CATEGORY_LABELS: Record<ScreeningRequirementCategory, string> = {
  qualification: '企业资质',
  performance: '项目业绩',
  personnel: '人员要求',
  financial: '财务要求',
  credit: '信誉要求',
  other: '其他要求',
};

export const CONCLUSION_DIMENSION_LABELS: Record<string, string> = {
  compliance: '合规风险',
  competition: '竞争与胜率',
  resource: '资源匹配',
  reward: '收益与投入',
  return: '收益与投入',
};

export const SOURCE_TYPE_LABELS: Record<ScreeningSourceType, string> = {
  text: '粘贴文本',
  file: '上传文件',
  url: '网址抓取',
};

export const PRESCREEN_LABELS: Record<ScreeningPrescreenStatus, string> = {
  none: '未初筛',
  keep: '建议细评',
  unsure: '待确认',
  drop: '已排除',
};

export const PRESCREEN_CLASSES: Record<ScreeningPrescreenStatus, string> = {
  none: 'bg-gray-100 text-gray-500 border-gray-200',
  keep: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  unsure: 'bg-amber-50 text-amber-700 border-amber-200',
  drop: 'bg-gray-100 text-gray-400 border-gray-200',
};

export const SCORING_MODE_LABELS: Record<ScreeningScoringMode, string> = {
  weighted: '按公告评分办法的分值计分',
  equal: '公告未给出分值，按等权口径计分',
  mandatory_only: '无评分项，按硬性门槛满足率计分',
};

export const OUTCOME_LABELS: Record<ScreeningOutcome, string> = {
  unknown: '未记录结果',
  won: '已中标',
  lost: '未中标',
  abandoned: '放弃投标',
};

export const OUTCOME_CLASSES: Record<ScreeningOutcome, string> = {
  unknown: 'bg-gray-100 text-gray-500 border-gray-200',
  won: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  lost: 'bg-rose-50 text-rose-700 border-rose-200',
  abandoned: 'bg-gray-100 text-gray-500 border-gray-200',
};

/** 从接口错误中提取可读信息 */
export const pickErrorMessage = (error: unknown, fallback: string): string => {
  const candidate = error as {
    response?: { data?: { detail?: unknown; message?: string } };
    message?: string;
  };
  const detail = candidate?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) {
    return detail;
  }
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0] as { msg?: string };
    if (first?.msg) {
      return first.msg;
    }
  }
  const message = candidate?.response?.data?.message;
  if (typeof message === 'string' && message.trim()) {
    return message;
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return fallback;
};
